import { Price } from '../../../primitive/price';
import { AttributeCode } from './attributeCode';
import { Schema, SchemaGetter } from 'effect';

/**
 * PriceFromSelf - Schema for Price values that are already branded
 *
 * This accepts Price values directly (already branded BigDecimal).
 * Use when working with Price values in state/props.
 */
const PriceFromSelf = Schema.BigDecimal.pipe(
  Schema.decodeTo(Schema.BigDecimal, {
    decode: SchemaGetter.transform((bd) => Price(bd)),
    encode: SchemaGetter.transform((price) => price),
  })
);

/**
 * ContinuousRangeValue - Continuous numeric range with BigDecimal
 *
 * Used for filters that accept any value within a range:
 * - SSD capacity (128GB - 2TB)
 * - Warranty period (6 months - 5 years)
 * - Battery life (4h - 20h)
 * - Screen size (13" - 17")
 *
 * Uses linear scale in UI slider.
 */
export const ContinuousRangeValue = Schema.TaggedStruct('ContinuousRange', {
  min: Schema.BigDecimal,
  max: Schema.BigDecimal,
});

export type ContinuousRangeValue = Schema.Schema.Type<typeof ContinuousRangeValue>;

/**
 * PriceRangeValue - Price range with branded Price type
 *
 * Used exclusively for price filters with:
 * - Branded Price type for type safety (prevents mixing with other numeric values)
 * - Quadratic scale in UI slider (better UX for price selection)
 * - Currency-aware formatting
 *
 * The Price brand ensures compile-time distinction from generic BigDecimal values.
 */
export const PriceRangeValue = Schema.TaggedStruct('PriceRange', {
  min: PriceFromSelf,
  max: PriceFromSelf,
});

export type PriceRangeValue = Schema.Schema.Type<typeof PriceRangeValue>;

/**
 * SteppedRangeValue - Discrete stepped range with predefined values
 *
 * For filters that only accept specific discrete values:
 * - RAM: [4GB, 8GB, 16GB, 32GB, 64GB]
 * - Storage tiers: [256GB, 512GB, 1TB, 2TB]
 * - Core counts: [2, 4, 6, 8, 12, 16]
 *
 * UI will snap slider to discrete values instead of continuous range.
 * Selected min/max must be present in steps array.
 *
 * @example
 * const ramFilter = makeSteppedRangeValue({
 *   min: BigDecimal.fromNumberUnsafe(8),
 *   max: BigDecimal.fromNumberUnsafe(32),
 *   steps: Chunk.make(4, 8, 16, 32, 64).pipe(Chunk.map(BigDecimal.fromNumberUnsafe))
 * });
 */
export const SteppedRangeValue = Schema.TaggedStruct('SteppedRange', {
  min: Schema.BigDecimal,
  max: Schema.BigDecimal,
  steps: Schema.Chunk(Schema.BigDecimal),
});

export type SteppedRangeValue = Schema.Schema.Type<typeof SteppedRangeValue>;

/**
 * RangeFilterValue - Discriminated union of all range filter types
 *
 * Pattern match on _tag to handle different range behaviors:
 * - 'ContinuousRange': Linear scale, any value in range
 * - 'PriceRange': Quadratic scale, branded Price type
 * - 'SteppedRange': Discrete values only, snap to predefined steps
 */
export const RangeFilterValue = Schema.Union([
  ContinuousRangeValue,
  PriceRangeValue,
  SteppedRangeValue,
]);

export type RangeFilterValue = Schema.Schema.Type<typeof RangeFilterValue>;

/**
 * Helper constructors for creating range filter values
 */

export const makeContinuousRangeValue = (params: {
  min: ContinuousRangeValue['min'];
  max: ContinuousRangeValue['max'];
}): ContinuousRangeValue =>
  Schema.decodeSync(ContinuousRangeValue)({
    _tag: 'ContinuousRange' as const,
    ...params,
  });

export const makePriceRangeValue = (params: {
  min: PriceRangeValue['min'];
  max: PriceRangeValue['max'];
}): PriceRangeValue =>
  Schema.decodeSync(PriceRangeValue)({
    _tag: 'PriceRange' as const,
    ...params,
  });

export const makeSteppedRangeValue = (params: {
  min: SteppedRangeValue['min'];
  max: SteppedRangeValue['max'];
  steps: SteppedRangeValue['steps'];
}): SteppedRangeValue =>
  Schema.decodeSync(SteppedRangeValue)({
    _tag: 'SteppedRange' as const,
    min: params.min,
    max: params.max,
    steps: params.steps,
  });

// Simplified structure: direct mapping from AttributeCode to RangeFilterValue
export const RangeFilterStateMap = Schema.HashMap(AttributeCode, RangeFilterValue);

export type RangeFilterStateMap = Schema.Schema.Type<typeof RangeFilterStateMap>;
